<template>
    <div class="appointments-management mt-5">

        <Snackbar
            v-for="notification in notifications"
            :key="notification.id"
            :type="notification.type"
            class="appointment-management-snackbar"
            v-on:close="removeNotification(notification.id)"
        >
            {{ notification.message }}
        </Snackbar>

        <div v-if="hasTimeRanges">
            <div class="d-flex justify-content-between align-items-center mb-2">
                <h3 class="mb-4">{{ calendarTitle }}</h3>

                <button
                    class="btn btn-blue"
                    v-if="canEdit"
                    @click="updateAppointments()"
                    :disabled="hasAnyDayOverlap()"
                >
                    {{ saveAllAppointmentsButton }}
                </button>
            </div>

            <div class="appointment-container">
                <div v-for="(timeRanges, day) in sortedTimeRanges" :key="day" class="appointment-day mb-4">
                    <div class="day-header">
                        <h4>{{ day }}</h4>
                    </div>

                    <div class="time-ranges">
                        <PauseBlock
                            v-if="hasOpeningPause(timeRanges, getOpeningHour())"
                            :pause-duration="calculateOpeningPauseDuration(getOpeningHour(), timeRanges[0].start_time)"
                            :start-time="getOpeningHour()"
                            :end-time="timeRanges[0].start_time"
                            :day="day"
                            :can-edit="canEdit"
                            @add-appointment="openNewAppointmentModal"
                        />

                        <!-- Normal appointments and pauses between them -->
                        <div v-for="(timeRange, index) in timeRanges" :key="`appointment-${day}-${index}`" class="time-slot">
                            <AppointmentBlock
                                :time-range="timeRange"
                                :is-expanded="expandedAppointments[`${day}-${index}`]"
                                :overlap="hasOverlap(timeRanges, index) || (index > 0 && hasOverlap(timeRanges, index - 1))"
                                :appointment-interview-label="appointmentInterviewLabel"
                                :appointment-booked-label="appointmentBookedLabel"
                                :appointment-end-time-label="appointmentEndTimeLabel"
                                :appointment-start-time-label="appointmentStartTimeLabel"
                                :can-edit="canEdit"
                                :booked-appointments="appointments"
                                @toggle-expand="toggleAppointmentInputs(`${day}-${index}`)"
                                @delete-request="deleteTimeRange"
                                @update-times="updateTimeRange"
                            />

                            <!-- Pause between appointments -->
                            <PauseBlock
                                v-if="hasPause(timeRanges, index)"
                                :pause-duration="calculatePauseDuration(timeRange, timeRanges[index+1])"
                                :start-time="timeRange.end_time"
                                :end-time="timeRanges[index+1].start_time"
                                :day="day"
                                :can-edit="canEdit"
                                @add-appointment="openNewAppointmentModal"
                            />
                        </div>

                        <PauseBlock
                            v-if="hasClosingPause(timeRanges, getClosingHour())"
                            :pause-duration="calculateClosingPauseDuration(timeRanges[timeRanges.length - 1].end_time, getClosingHour())"
                            :start-time="timeRanges[timeRanges.length - 1].end_time"
                            :end-time="getClosingHour()"
                            :day="day"
                            :can-edit="canEdit"
                            @add-appointment="openNewAppointmentModal"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="no-appointments">
            <p>{{ noAppointmentsMessage }}</p>
        </div>

        <DeleteConfirmationModal
            :show="showDeleteModal"
            :time-range="timeRangeToDelete"
            @close="closeDeleteModal"
            @confirm="confirmDeleteTimeRange"
        />

        <NewAppointmentModal
            :show="showModal"
            :appointment="newAppointment"
            @close="closeModal"
            @submit="createNewAppointment"
        />
    </div>
</template>

<script>
import PauseBlock from './PauseBlock.vue';
import AppointmentBlock from './AppointmentBlock.vue';
import DeleteConfirmationModal from './DeleteConfirmationModal.vue';
import NewAppointmentModal from './NewAppointmentModal.vue';
import timeMixin from "../../Mixins/appointmentsMixin";
import Snackbar from "../../AppNotifications/Snackbar.vue";

export default {
    name: "AppointmentsManagement",
    components: {
        PauseBlock,
        AppointmentBlock,
        DeleteConfirmationModal,
        NewAppointmentModal,
        Snackbar
    },
    mixins: [timeMixin],
    props: {
        lodge: {
            type: Object,
            required: true,
        },
        company: {
            type: Object,
            required: true,
        },
        calendarTitle: {
            type: String,
            default: 'Calendrier'
        },
        canEdit: {
            type: Boolean,
            default: true
        },
        appointments: null,
        saveAllAppointmentsButton: String,
        noAppointmentsMessage: String,
        successAppointmentSavedMessage: String,
        errorAppointmentSavedMessage: String,
        successAppointmentCreatedMessage: String,
        errorAppointmentCreatedMessage: String,
        successAppointmentDeletedMessage: String,
        errorAppointmentDeletedMessage: String,

        appointmentInterviewLabel: String,
        appointmentBookedLabel: String,
        appointmentStartTimeLabel: String,
        appointmentEndTimeLabel: String,
    },
    data() {
        return {
            computedLodge: JSON.parse(JSON.stringify(this.lodge)),
            expandedAppointments: {},
            showModal: false,
            showDeleteModal: false,
            timeRangeToDelete: null,
            newAppointment: {
                day: '',
                start_time: '',
                end_time: ''
            },
            sortedTimeRanges: {},
            debouncedUpdate: null,
            notifications: [],
            notificationCounter: 0
        }
    },
    watch: {
        lodge: {
            handler(newValue) {
                this.computedLodge = JSON.parse(JSON.stringify(newValue));
                this.sortTimeRangesData();
            },
            deep: true
        }
    },
    computed: {
        hasTimeRanges() {
            return this.computedLodge && this.computedLodge.day_time_ranges && this.computedLodge.day_time_ranges.length > 0;
        }
    },
    mounted() {
        this.sortTimeRangesData();
    },
    methods: {
        sortTimeRangesData() {
            this.sortedTimeRanges = this.sortTimeRanges(this.computedLodge.day_time_ranges);
        },

        getOpeningHour() {
            return this.lodge.config.opening_hour;
        },

        getClosingHour() {
            return this.lodge.config.closing_hour;
        },

        toggleAppointmentInputs(appointmentId) {
            this.$set(
                this.expandedAppointments,
                appointmentId,
                !this.expandedAppointments[appointmentId]
            );
        },

        openNewAppointmentModal(day, pauseStartTime, pauseEndTime) {
            this.newAppointment = {
                day: day,
                start_time: pauseStartTime,
                end_time: pauseEndTime
            };

            this.showModal = true;
        },

        closeModal() {
            this.showModal = false;
        },

        deleteTimeRange(timeRange) {
            this.timeRangeToDelete = timeRange;
            this.showDeleteModal = true;
        },

        closeDeleteModal() {
            this.showDeleteModal = false;
            this.timeRangeToDelete = null;
        },

        updateTimeRange(updatedTimeRange) {
            const dayRanges = this.sortedTimeRanges[updatedTimeRange.day];

            if (dayRanges) {
                const index = dayRanges.findIndex(tr => tr.id === updatedTimeRange.id);
                if (index !== -1) {
                    this.$set(dayRanges, index, updatedTimeRange);
                    const lodgeIndex = this.computedLodge.day_time_ranges.findIndex(tr => tr.id === updatedTimeRange.id);
                    if (lodgeIndex !== -1) {
                        this.$set(this.computedLodge.day_time_ranges, lodgeIndex, updatedTimeRange);
                    }
                }
            }
        },

        async confirmDeleteTimeRange() {
            if (!this.timeRangeToDelete) return;

            try {
                const response = await axios.delete(`/company/${this.company.id}/lodges/${this.computedLodge.id}/${this.timeRangeToDelete.id}/delete`);

                if (response.data && response.data.lodge) {
                    this.computedLodge = response.data.lodge;
                    this.sortTimeRangesData();
                    this.addNotification('success', this.successAppointmentDeletedMessage);
                }
            } catch (err) {
                console.error('Error deleting appointment:', err);
                this.addNotification('error', this.errorAppointmentDeletedMessage);
            } finally {
                this.closeDeleteModal();
            }
        },

        async updateAppointments() {
            try {
                const response = await axios.put(`/company/${this.company.id}/lodges/${this.computedLodge.id}/appointments/update`, {
                    appointments: this.computedLodge.day_time_ranges.map(range => ({
                        id: range.id,
                        start_time: this.formatTime(range.start_time),
                        end_time: this.formatTime(range.end_time)
                    }))
                });

                if (response.status === 200) {
                    this.computedLodge = response.data.lodge;
                    this.addNotification('success', this.successAppointmentSavedMessage);
                }
            } catch (err) {
                console.error(err);
                this.addNotification('error', this.errorAppointmentSavedMessage);
            }
        },

        async createNewAppointment(appointmentData) {
            try {
                const response = await axios.post(`/company/${this.company.id}/lodges/${this.computedLodge.id}/appointments/create`, {
                    day: appointmentData.day,
                    start_time: this.formatTime(appointmentData.start_time),
                    end_time: this.formatTime(appointmentData.end_time)
                });

                if (response.data.success) {
                    this.computedLodge = response.data.lodge;
                    this.sortTimeRangesData();
                    this.closeModal();
                    this.addNotification('success', this.successAppointmentCreatedMessage);
                }
            } catch (err) {
                console.error(err);
                this.addNotification('error', this.errorAppointmentCreatedMessage);
            }
        },

        addNotification(type, message) {
            const id = this.notificationCounter++;
            this.notifications.push({id, type, message});
        },

        removeNotification(id) {
            const index = this.notifications.findIndex(n => n.id === id);

            if (index !== -1) {
                this.notifications.splice(index, 1);
            }
        }
    }
}
</script>

